/**
 * Settings › Fields: every field each kind of record has, per record type.
 *
 * One tab per record type, in the organisation's words, leads first. A
 * field someone has described (a property definition: label, type, options)
 * is listed under its group, in order; the arrows move one within its group.
 * Archive hides a description everywhere and keeps every stored value;
 * delete is only for one nothing holds a value for (the server says so).
 *
 * The leads tab also lists every field seen on leads (`GET
 * /api/v1/lead-fields`, every campaign) with how many leads carry it: the
 * default fields every lead has, and the custom ones nobody has described
 * yet, each with "Add a label and type", which opens the same drawer with
 * the key filled in. A described field shows how many leads carry it too.
 *
 * With CRM switched off only the leads tab is here: lead fields are the data
 * leads arrive with, which distribution uses on its own.
 */
import { useMemo, useState } from 'react';
import {
  ActionIcon, Alert, Badge, Button, Card, Group, Menu, Modal, Stack, Switch, Table, Tabs, Text, Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconArchive, IconArrowBackUp, IconArrowDown, IconArrowUp, IconDots, IconPencil, IconPlus, IconTrash,
  IconWand,
} from '@tabler/icons-react';
import { useSearchParams } from 'react-router-dom';
import {
  useProperties, usePropertyEdits, type PropertyDef, type PropertyObject, type SeedPropertiesResponse,
} from '../../api/properties';
import { useLeadFields, type LeadField } from '../../api/leadFields';
import { FONTS } from '../../theme';
import { MonoLabel } from '../../ui';
import { useVocab, type Vocab } from '../../vocab';
import { fetchUsedBy } from '../../api/workflows';
import { activeNames } from '../workflows/UsedByPanel';
import { builtInLabel, coverageHint } from '../SmartFilterEditor';
import { PropertyDrawer } from './PropertyDrawer';
import { groupDefs, hasOptions, objectWord, TYPE_LABEL } from './propertyModel';

/** The tabs, in the order people look for them: leads first. */
const TAB_ORDER: PropertyObject[] = ['submission', 'person', 'company', 'deal'];

/** A seen lead field as the Fields tab lists it. */
export type SeenFieldRow = { key: string; label: string; hint: string };

/**
 * The leads tab's seen fields, split: the default fields every lead has
 * (with their names), and the custom keys no description covers yet,
 * commonest first as the server sends them. A key with a description,
 * archived ones included, is the described list's, so it is left out here.
 */
export function seenFieldRows(
  v: Vocab,
  fields: LeadField[],
  describedKeys: Set<string>,
): { defaults: SeenFieldRow[]; undescribed: SeenFieldRow[] } {
  const row = (f: LeadField, label: string): SeenFieldRow => ({ key: f.key, label, hint: coverageHint(v, f) });
  return {
    defaults: fields.filter((f) => f.built_in).map((f) => row(f, builtInLabel(v, f.key))),
    undescribed: fields.filter((f) => !f.built_in && !describedKeys.has(f.key)).map((f) => row(f, f.key)),
  };
}

/** Whether a label says nothing its key does not ("Source" for `source`). */
function sameWords(label: string, key: string): boolean {
  const plain = (x: string) => x.toLowerCase().replace(/[_\s]+/g, ' ').trim();
  return plain(label) === plain(key);
}

export function PropertiesSettingsTab() {
  const v = useVocab();
  const objects: PropertyObject[] = TAB_ORDER;
  const [params, setParams] = useSearchParams();
  const wanted = params.get('object') as PropertyObject | null;
  const object: PropertyObject = wanted && objects.includes(wanted) ? wanted : (objects[0] ?? 'submission');

  return (
    <Stack gap="md" maw={900}>
      <Text size="sm" c="dimmed">
        {`The fields each kind of record has, for the whole organisation. Every field seen on ${v.many('lead')} is listed under ${v.Many('lead')}; give one a label and a type to make it easier to read. Values are never changed, and search, filters, imports and routing filters all use the same names.`}
      </Text>
      <Tabs
        value={object}
        onChange={(o) => o && setParams((p) => { p.set('object', o); return p; }, { replace: true })}
        keepMounted={false}
        variant="pills"
      >
        <Tabs.List>
          {objects.map((o) => <Tabs.Tab key={o} value={o}>{objectWord(v, o)}</Tabs.Tab>)}
        </Tabs.List>
        {objects.map((o) => (
          <Tabs.Panel key={o} value={o} pt="md">
            <ObjectFields object={o} />
          </Tabs.Panel>
        ))}
      </Tabs>
    </Stack>
  );
}

function ObjectFields({ object }: { object: PropertyObject }) {
  const v = useVocab();
  const isLead = object === 'submission';
  const [archived, setArchived] = useState(false);
  const list = useProperties(object, { archived: true });
  const seen = useLeadFields(undefined, isLead);
  const edits = usePropertyEdits();
  const [editing, setEditing] = useState<PropertyDef | null>(null);
  const [creating, setCreating] = useState(false);
  const [describing, setDescribing] = useState<string | null>(null);
  const [seeding, setSeeding] = useState(false);
  const all = useMemo(() => list.data?.properties ?? [], [list.data]);
  const live = all.filter((d) => !d.archived);
  const gone = all.filter((d) => d.archived);
  const groups = groupDefs(live);
  const groupNames = [...new Set(all.map((d) => d.group_name).filter(Boolean))];
  const hints = useMemo(() => {
    const out: Record<string, string> = {};
    for (const f of seen.data?.fields ?? []) out[f.key] = coverageHint(v, f);
    return out;
  }, [seen.data, v]);
  const { defaults, undescribed } = useMemo(
    () => (isLead && seen.data
      ? seenFieldRows(v, seen.data.fields, new Set(all.map((d) => d.key)))
      : { defaults: [], undescribed: [] }),
    [isLead, seen.data, all, v],
  );

  /** Move one field up or down within its group, keeping the rest. */
  async function move(def: PropertyDef, by: -1 | 1) {
    const ordered = groups.flatMap((g) => g.defs);
    const group = groups.find((g) => g.defs.some((d) => d.id === def.id));
    if (!group) return;
    const i = group.defs.findIndex((d) => d.id === def.id);
    const other = group.defs[i + by];
    if (!other) return;
    const ids = ordered.map((d) => d.id);
    const a = ids.indexOf(def.id);
    const b = ids.indexOf(other.id);
    [ids[a], ids[b]] = [ids[b] ?? '', ids[a] ?? ''];
    try {
      await edits.reorder.mutateAsync({ objectType: object, ids: [...ids, ...gone.map((d) => d.id)] });
    } catch (e) {
      notifications.show({ color: 'red', title: 'Not moved', message: (e as Error).message });
    }
  }

  async function setArchivedOn(def: PropertyDef, on: boolean) {
    if (on) {
      // Workflows that set or test it would fail on it: say which first.
      const used = await fetchUsedBy('property', `${object}:${def.key}`).catch(() => null);
      const names = activeNames(used);
      if (names.length > 0 && !window.confirm(
        `${names.length} active ${v.word(names.length, 'workflow')} ${names.length === 1 ? 'uses' : 'use'} ${def.label}: ${names.map((n) => `'${n}'`).join(', ')}. A step that sets it will fail once it is archived. Archive anyway?`,
      )) return;
    }
    try {
      await edits.archive.mutateAsync({ id: def.id, archived: on });
      notifications.show({
        color: 'teal',
        title: on ? 'Archived' : 'Restored',
        message: on ? `${def.label} is hidden. Stored values are kept.` : `${def.label} is back.`,
      });
    } catch (e) {
      notifications.show({ color: 'red', title: 'Not changed', message: (e as Error).message });
    }
  }

  async function del(def: PropertyDef) {
    if (!window.confirm(`Delete ${def.label}? This cannot be undone.`)) return;
    try {
      await edits.remove.mutateAsync(def.id);
      notifications.show({ color: 'teal', title: 'Deleted', message: def.label });
    } catch (e) {
      notifications.show({ color: 'red', title: 'Not deleted', message: (e as Error).message });
    }
  }

  const nameCell = (label: string, key: string) => (
    <Table.Td w="45%">
      <Text size="sm" fw={500} truncate="end">{label}</Text>
      {!sameWords(label, key) && (
        <Text size="xs" c="dimmed" truncate="end" style={{ fontFamily: FONTS.mono }}>{key}</Text>
      )}
    </Table.Td>
  );

  const row = (d: PropertyDef, i: number, n: number) => (
    <Table.Tr key={d.id} data-testid={`property-row-${d.key}`} style={d.archived ? { opacity: 0.6 } : undefined}>
      {nameCell(d.label, d.key)}
      <Table.Td>
        <Group gap={4} wrap="wrap">
          <Badge size="sm" variant="light" color="gray" radius="sm" tt="none">{TYPE_LABEL[d.type]}</Badge>
          {hasOptions(d.type) && <Text size="xs" c="dimmed">{`${d.options.length} options`}</Text>}
          {d.required && <Badge size="sm" variant="outline" color="chartAmber" radius="sm" tt="none">Required</Badge>}
        </Group>
        {isLead && hints[d.key] && <Text size="xs" c="dimmed" mt={2}>{hints[d.key]}</Text>}
      </Table.Td>
      <Table.Td w={190}>
        <Group gap={2} justify="flex-end" wrap="nowrap">
          {!d.archived && (
            <>
              <ActionIcon variant="subtle" size="sm" aria-label={`Move ${d.label} up`} disabled={i === 0} onClick={() => move(d, -1)}>
                <IconArrowUp size={14} />
              </ActionIcon>
              <ActionIcon variant="subtle" size="sm" aria-label={`Move ${d.label} down`} disabled={i === n - 1} onClick={() => move(d, 1)}>
                <IconArrowDown size={14} />
              </ActionIcon>
            </>
          )}
          <ActionIcon variant="subtle" size="sm" aria-label={`Edit ${d.label}`} onClick={() => setEditing(d)}>
            <IconPencil size={14} />
          </ActionIcon>
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <ActionIcon variant="subtle" size="sm" aria-label={`More for ${d.label}`}><IconDots size={14} /></ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              {d.archived ? (
                <Menu.Item leftSection={<IconArrowBackUp size={14} />} onClick={() => setArchivedOn(d, false)}>Restore</Menu.Item>
              ) : (
                <Menu.Item leftSection={<IconArchive size={14} />} onClick={() => setArchivedOn(d, true)}>Archive</Menu.Item>
              )}
              <Menu.Item color="red" leftSection={<IconTrash size={14} />} onClick={() => del(d)}>Delete</Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Table.Td>
    </Table.Tr>
  );

  const heading = (title: string, count: number, note?: string) => (
    <Stack gap={0} px="md" pt="sm" pb={4}>
      <Group gap={8}>
        <Text size="sm" fw={600}>{title}</Text>
        <MonoLabel>{count}</MonoLabel>
      </Group>
      {note && <Text size="xs" c="dimmed">{note}</Text>}
    </Stack>
  );

  const nothing = !list.isPending && live.length === 0 && (!isLead || (!seen.isPending && defaults.length + undescribed.length === 0));

  return (
    <Stack gap="md">
      <Group justify="space-between" wrap="wrap">
        <Group gap="sm">
          <Button leftSection={<IconPlus size={16} />} onClick={() => setCreating(true)}>
            {`New ${v.one('property')}`}
          </Button>
          {isLead && (
            <Button variant="default" leftSection={<IconWand size={16} />} onClick={() => setSeeding(true)}>
              {`Describe from ${v.one('campaign')} fields`}
            </Button>
          )}
        </Group>
        {gone.length > 0 && (
          <Switch
            size="xs"
            label={`Show archived (${gone.length})`}
            checked={archived}
            onChange={(e) => setArchived(e.currentTarget.checked)}
          />
        )}
      </Group>
      {list.error && <Alert color="red" variant="light">{list.error.message}</Alert>}
      {isLead && seen.error && (
        <Alert color="chartAmber" variant="light">
          {`The fields seen on ${v.many('lead')} could not be read, so only described ones are listed.`}
        </Alert>
      )}
      {nothing && (
        <Card p="lg">
          <Text size="sm" c="dimmed" ta="center">
            {isLead
              ? `No ${v.one('lead')} fields yet. They appear here as ${v.many('lead')} arrive.`
              : `No fields on ${objectWord(v, object).toLowerCase()} yet.`}
          </Text>
        </Card>
      )}
      {defaults.length > 0 && (
        <Card p={0} data-testid="fields-defaults">
          {heading('Default fields', defaults.length, `Every ${v.one('lead')} has these.`)}
          <Table verticalSpacing={6} layout="fixed">
            <Table.Tbody>
              {defaults.map((f) => (
                <Table.Tr key={f.key} data-testid={`field-row-${f.key}`}>
                  {nameCell(f.label, f.key)}
                  <Table.Td>
                    <Badge size="sm" variant="light" color="gray" radius="sm" tt="none">Default field</Badge>
                    {f.hint && <Text size="xs" c="dimmed" mt={2}>{f.hint}</Text>}
                  </Table.Td>
                  <Table.Td w={190} />
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Card>
      )}
      {groups.map(({ group, defs }) => (
        <Card key={group || 'none'} p={0} data-testid={`property-group-${group || 'none'}`}>
          {group
            ? heading(group, defs.length)
            : isLead && heading('Described', defs.length)}
          <Table verticalSpacing={6} layout="fixed">
            <Table.Tbody>{defs.map((d, i) => row(d, i, defs.length))}</Table.Tbody>
          </Table>
        </Card>
      ))}
      {undescribed.length > 0 && (
        <Card p={0} data-testid="fields-undescribed">
          {heading(
            `Seen on ${v.many('lead')}`,
            undescribed.length,
            'Give one a label and a type to name it in filters, search and imports.',
          )}
          <Table verticalSpacing={6} layout="fixed">
            <Table.Tbody>
              {undescribed.map((f) => (
                <Table.Tr key={f.key} data-testid={`field-row-${f.key}`}>
                  <Table.Td w="45%">
                    <Text size="sm" fw={500} truncate="end" style={{ fontFamily: FONTS.mono }}>{f.key}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">{f.hint}</Text>
                  </Table.Td>
                  <Table.Td w={190}>
                    <Group justify="flex-end">
                      <Button
                        size="compact-xs"
                        variant="light"
                        aria-label={`Add a label and type to ${f.key}`}
                        onClick={() => setDescribing(f.key)}
                      >
                        Add a label and type
                      </Button>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Card>
      )}
      {archived && gone.length > 0 && (
        <Card p={0}>
          <Group px="md" pt="sm" pb={4}><Text size="sm" fw={600} c="dimmed">Archived</Text></Group>
          <Table verticalSpacing={6} layout="fixed">
            <Table.Tbody>{gone.map((d, i) => row(d, i, gone.length))}</Table.Tbody>
          </Table>
        </Card>
      )}

      <PropertyDrawer
        opened={creating || !!editing || !!describing}
        object={object}
        def={editing}
        initialKey={editing ? null : describing}
        groups={groupNames}
        onClose={() => { setCreating(false); setEditing(null); setDescribing(null); }}
      />
      {isLead && <SeedModal opened={seeding} onClose={() => setSeeding(false)} />}
    </Stack>
  );
}

/**
 * "Describe from campaign fields": a dry run first, saying exactly what
 * would be described and what is left alone, then the real thing. Running
 * it again describes nothing new.
 */
function SeedModal({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const v = useVocab();
  const { seed } = usePropertyEdits();
  const [preview, setPreview] = useState<SeedPropertiesResponse | null>(null);
  const [error, setError] = useState('');

  async function look() {
    setError('');
    try {
      setPreview(await seed.mutateAsync(true));
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function create() {
    setError('');
    try {
      const r = await seed.mutateAsync(false);
      notifications.show({
        color: 'teal',
        title: 'Described',
        message: `${r.created.length} ${r.created.length === 1 ? v.one('property') : v.many('property')}`,
      });
      setPreview(null);
      onClose();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={() => { setPreview(null); onClose(); }}
      title={`Describe from ${v.one('campaign')} fields`}
      size="lg"
    >
      <Stack>
        <Text size="sm">
          {`Each ${v.one('campaign')}'s declared fields, then the fields found on its ${v.many('lead')}, get a label and a type. Types come from the field, or from a look at recent ${v.many('lead')}. A field already described is left alone.`}
        </Text>
        {error && <Alert color="red" variant="light">{error}</Alert>}
        {!preview && (
          <Group justify="flex-end">
            <Button onClick={look} loading={seed.isPending}>See what it would describe</Button>
          </Group>
        )}
        {preview && (
          <>
            {!preview.sampled && preview.created.length > 0 && (
              <Alert color="chartAmber" variant="light">
                <Text size="sm">{`Recent ${v.many('lead')} could not be read, so types come from the fields alone.`}</Text>
              </Alert>
            )}
            {preview.created.length === 0 ? (
              <Text size="sm" c="dimmed">Nothing new to describe.</Text>
            ) : (
              <Table verticalSpacing={4} data-testid="seed-preview">
                <Table.Thead>
                  <Table.Tr><Table.Th>Key</Table.Th><Table.Th>Label</Table.Th><Table.Th>Type</Table.Th><Table.Th>Group</Table.Th></Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {preview.created.map((d) => (
                    <Table.Tr key={d.key}>
                      <Table.Td><Text size="xs" style={{ fontFamily: FONTS.mono }}>{d.key}</Text></Table.Td>
                      <Table.Td><Text size="sm">{d.label}</Text></Table.Td>
                      <Table.Td>
                        <Tooltip label={d.options.map((o) => o.value).join(', ')} disabled={!d.options.length} withArrow multiline maw={320}>
                          <Text size="sm">{TYPE_LABEL[d.type]}{d.options.length ? ` (${d.options.length})` : ''}</Text>
                        </Tooltip>
                      </Table.Td>
                      <Table.Td><Text size="sm" c="dimmed">{d.group_name}</Text></Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
            {preview.skipped.length > 0 && (
              <Text size="xs" c="dimmed">
                {`Left alone: ${preview.skipped.map((s) => `${s.key} (${s.reason})`).join(', ')}.`}
              </Text>
            )}
            <Group justify="flex-end">
              <Button variant="subtle" onClick={() => { setPreview(null); onClose(); }}>Cancel</Button>
              <Button disabled={preview.created.length === 0} loading={seed.isPending} onClick={create}>
                {`Describe ${preview.created.length}`}
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
}
